/**
 * Medicines counted on the shelf before their batch numbers and expiry dates
 * were written down. A count is not stock: it is never on hand or on the
 * till. Each item leaves this list as a pharmacist records its batches as
 * opening stock.
 *
 * The owner may include the count in the inventory figures, labelled
 * "Counted stock — batch/expiry pending" (0169). That is recorded once, with a
 * reason, and changes nothing about what can be sold.
 *
 * Clearing it: pick items and "Record batches" opens Add stock with each
 * one's awaiting quantity and counted cost filled in; the batch number,
 * expiry and storage are typed from the pack. The stock goes on hand at
 * once, each batch held until a registered pharmacist releases it for sale. An item a pharmacist has since found is not a medicine needs no
 * batch: "Add to stock" adds what was counted, at its counted cost.
 */

import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { money } from '../../lib/format';
import { useAuth } from '../../lib/auth';
import { Badge, Button, Card, CardHeader, Input, Modal, Textarea } from '../../components/ui';
import { usePharmacy, usePharmacyWrite } from './data';
import { ErrorLine, Table, Td, Tile } from './parts';

export interface CountedStockRecognition {
  recognisedAt: string;
  recognisedBy: string | null;
  reason: string;
  products: number;
  units: number;
  value: string;
}

/** One counted item, as Add stock receives it to fill in a line. */
export interface CountedLine {
  productId: string;
  branchId: string;
  awaiting: number;
  unitCost: string;
}

interface CountedRow {
  id: string;
  productId: string;
  name: string;
  sku: string | null;
  branchId: string;
  branchName: string;
  counted: number;
  unitCost: string;
  batchesRecorded: number;
  awaiting: number;
  awaitingValue: string;
  isMedicine?: boolean;
  tracksBatches?: boolean;
  storageRequirement?: string | null;
}

interface CountedStock {
  recognition?: CountedStockRecognition | null;
  data: Array<CountedRow & {
    branchName: string;
    counted: number;
    unitCost: string;
    batchesRecorded: number;
    awaiting: number;
    awaitingValue: string;
  }>;
  totals: { products: number; units: number; value: string };
}

export function CountedStockCard({ branchId, currency }: { branchId: string | null | undefined; currency: string }): ReactNode {
  const { business, can } = useAuth();
  const navigate = useNavigate();
  const counted = usePharmacy<CountedStock>('/counted-stock', { branchId });
  const [filter, setFilter] = useState('');
  const [recognising, setRecognising] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [added, setAdded] = useState<Record<string, 'added' | 'awaiting approval'>>({});
  const addToStock = usePharmacyWrite<Record<string, unknown>, { id: string; posted?: boolean }>('post', '/stock-entries');
  const mayEnter = can('pharmacy:stock');
  const t = counted.data?.totals;
  if (!t || t.products === 0) return null;
  const recognition = counted.data!.recognition ?? null;
  const rows = counted.data!.data.filter(
    (r) => r.awaiting > 0 && (!filter || `${r.name} ${r.sku ?? ''}`.toLowerCase().includes(filter.toLowerCase())),
  );
  // Batches are recorded for what is still batch-tracked; an item found not to be a medicine is added as counted.
  const needsBatches = rows.filter((r) => r.tracksBatches !== false);
  const picked = needsBatches.filter((r) => selected.has(r.id));
  const toggle = (id: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const recordBatches = () => {
    const lines: CountedLine[] = picked.map((r) => ({ productId: r.productId, branchId: r.branchId, awaiting: r.awaiting, unitCost: r.unitCost }));
    navigate('/pharmacy/stock/entry', { state: { counted: lines } });
  };
  const downloadSheet = () => {
    const blob = new Blob([`\uFEFF${batchSheetCsv(picked.length ? picked : needsBatches)}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'batch-sheet.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };
  const add = (r: CountedRow) =>
    addToStock.mutate(
      { branchId: r.branchId, lines: [{ productId: r.productId, quantity: r.awaiting, unitCost: r.unitCost }] },
      { onSuccess: (run) => setAdded((a) => ({ ...a, [r.id]: run.posted ? 'added' : 'awaiting approval' })) },
    );

  return (
    <Card padded={false}>
      <div className="space-y-3 p-4 sm:p-5">
        <CardHeader
          title="Counted stock — batch/expiry pending"
          subtitle="Counted on the shelf and shown in inventory as recorded stock; batch numbers and expiry dates are still to be entered. Not on the till and not for sale until its batches, expiry and storage are recorded and a registered pharmacist releases it. Enter batches any time with Record batches or the batch sheet: the count is never imported again, it is drawn down."
          action={<Input aria-label="Filter counted items" placeholder="Filter by item" value={filter} onChange={(e) => setFilter(e.target.value)} />}
        />
        <section aria-label="Counted stock" className="grid grid-cols-3 gap-3">
          <Tile label="Items" value={t.products} />
          <Tile label="Units" value={t.units} />
          <Tile
            label="At cost"
            value={money(t.value, currency)}
            sub={recognition ? 'in inventory value, awaiting verification' : 'not in stock value'}
          />
        </section>
        {recognition ? (
          <p className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
            <Badge tone="warning">In inventory value</Badge>
            Included by {recognition.recognisedBy ?? 'the owner'} on {new Date(recognition.recognisedAt).toLocaleDateString()}: “{recognition.reason}”. Still not for sale.
          </p>
        ) : (
          business?.role === 'owner' && (
            <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
              <span>Not in the inventory value yet.</span>
              <Button variant="secondary" onClick={() => setRecognising(true)}>Include in inventory value</Button>
            </div>
          )
        )}
        {mayEnter && (
          <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
            <Button disabled={picked.length === 0} onClick={recordBatches}>
              Record batches{picked.length ? ` for ${picked.length}` : ''}
            </Button>
            <Button variant="secondary" disabled={needsBatches.length === 0} onClick={downloadSheet}>
              Download batch sheet ({picked.length || needsBatches.length})
            </Button>
            <span>
              Quantity and cost are filled in from the count; type the batch number and expiry from each pack. It goes into stock at once, held until a registered pharmacist releases it for sale.
              On paper or a laptop: fill in the batch sheet (one row per batch; split a quantity across rows when packs differ) and upload it under Settings → Import as opening stock.
            </span>
          </div>
        )}
        <ErrorLine error={addToStock.error} />
      </div>
      <Table
        head={[
          mayEnter ? (
            <input
              key="all"
              type="checkbox"
              aria-label="Select every item shown"
              checked={needsBatches.length > 0 && needsBatches.every((r) => selected.has(r.id))}
              onChange={(e) => setSelected(e.target.checked ? new Set(needsBatches.map((r) => r.id)) : new Set())}
            />
          ) : (
            ''
          ),
          'Item',
          'Location',
          'Counted',
          'Batches recorded',
          'Awaiting',
          'Cost',
          'Value',
        ]}
        minWidth={820}
      >
        {rows.map((r) => (
          <tr key={r.id}>
            <Td>
              {mayEnter && r.tracksBatches !== false && (
                <input type="checkbox" aria-label={`Select ${r.name}`} checked={selected.has(r.id)} onChange={() => toggle(r.id)} />
              )}
            </Td>
            <Td>
              {r.name}
              {r.sku && <p className="font-mono text-xs text-slate-500">{r.sku}</p>}
              {r.tracksBatches === false && (
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <Badge tone="info">Not a medicine: no batch needed</Badge>
                  {added[r.id] ? (
                    <span className="text-xs text-emerald-700">{added[r.id] === 'added' ? 'Added to stock' : 'Sent for approval'}</span>
                  ) : (
                    mayEnter && (
                      <Button size="sm" variant="secondary" loading={addToStock.isPending} onClick={() => add(r)}>
                        Add {r.awaiting} to stock
                      </Button>
                    )
                  )}
                </div>
              )}
            </Td>
            <Td>{r.branchName}</Td>
            <Td className="tabular-nums">{r.counted}</Td>
            <Td className="tabular-nums">{r.batchesRecorded}</Td>
            <Td className="tabular-nums font-medium text-amber-700">{r.awaiting}</Td>
            <Td className="tabular-nums">{money(r.unitCost, currency)}</Td>
            <Td className="tabular-nums">{money(r.awaitingValue, currency)}</Td>
          </tr>
        ))}
      </Table>
      {recognising && <RecogniseModal totals={t} currency={currency} onClose={() => setRecognising(false)} />}
    </Card>
  );
}

function RecogniseModal({
  totals,
  currency,
  onClose,
}: {
  totals: CountedStock['totals'];
  currency: string;
  onClose: () => void;
}): ReactNode {
  const [reason, setReason] = useState('');
  const save = usePharmacyWrite<{ reason: string }, CountedStockRecognition>('post', '/counted-stock/recognition');
  return (
    <Modal open onClose={onClose} title="Include counted stock in inventory value">
      <div className="space-y-3">
        {save.data ? (
          <>
            <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
              Included: {save.data.products} item(s), {save.data.units} unit(s), {money(save.data.value, currency)} at cost, shown as awaiting batch/expiry verification.
            </p>
            <div className="flex justify-end"><Button onClick={onClose}>Close</Button></div>
          </>
        ) : (
          <>
            <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">
              <strong>{totals.products} item(s), {totals.units} unit(s), {money(totals.value, currency)}</strong> at cost will show in inventory and stock value as
              “Counted stock — batch/expiry pending”, apart from saleable stock.
            </p>
            <p className="text-sm text-slate-600">
              Nothing becomes on hand or sellable. Each item still needs its batch numbers, expiry dates and storage recorded and a pharmacist's approval. This is recorded once, under your name.
            </p>
            <Textarea label="Why" value={reason} onChange={(e) => setReason(e.target.value)} />
            <ErrorLine error={save.error} />
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={onClose}>Cancel</Button>
              <Button disabled={reason.trim().length < 5} loading={save.isPending} onClick={() => save.mutate({ reason: reason.trim() })}>
                Include in inventory value
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

const CSV_HEADERS = ['Product', 'Description', 'Quantity', 'Unit cost', 'Batch number', 'Expiry date', 'Storage confirmed'];

/**
 * The counted items as an opening-stock sheet to fill in from the packs: the
 * product by its code, the counted quantity and cost; batch, expiry and (for
 * a cold item) storage left blank — never guessed.
 */
export function batchSheetCsv(rows: Array<Pick<CountedRow, 'name' | 'sku' | 'awaiting' | 'unitCost'>>): string {
  const cell = (v: string | number) => {
    const t = String(v);
    return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
  };
  const lines = rows.map((r) => [r.sku || r.name, r.name, r.awaiting, r.unitCost ?? '', '', '', ''].map(cell).join(','));
  return [CSV_HEADERS.join(','), ...lines].join('\n') + '\n';
}
